/**
 * @fileoverview AnimationPreset → CSS keyframe registry for in-screen element
 * animations (schema v2.17+).
 * @module screen-view-v2/animations/presetRegistry
 *
 * v1 scope: entrance (26) + emphasis (14) + continuous/loop (12) presets.
 * Exit presets (fadeOut…) belong to deferred triggers (onExit) and resolve to
 * null. Unknown values also resolve to null with a one-time console.warn —
 * an animation must never crash a screen.
 *
 * The physical keyframes live in element-animations.css. Class name contract:
 * `.sv2-anim-<preset>` sets `animation-name: sv2-kf-<preset>`; timing comes
 * from `--sv2-anim-*` custom properties (see elementAnimationSpec.ts).
 */

export type PresetCategory = 'entrance' | 'emphasis' | 'loop';

export interface PresetDefinition {
  category: PresetCategory;
  /** Default animation-duration when timing.duration is not provided (ms) */
  defaultDurationMs: number;
  /** Default easing when timing.easing is not provided */
  defaultEasing?: string;
}

const ENTRANCE = (defaultDurationMs = 450): PresetDefinition => ({
  category: 'entrance',
  defaultDurationMs,
});

const EMPHASIS = (defaultDurationMs = 800): PresetDefinition => ({
  category: 'emphasis',
  defaultDurationMs,
});

const LOOP = (defaultDurationMs = 2400): PresetDefinition => ({
  category: 'loop',
  defaultDurationMs,
  defaultEasing: 'ease-in-out',
});

/** In-scope presets. Key = schema AnimationPreset value. */
export const PRESET_REGISTRY: Record<string, PresetDefinition> = {
  // ── Entrance (26) ─────────────────────────────────────────────
  fadeIn: ENTRANCE(400),
  fadeInUp: ENTRANCE(),
  fadeInDown: ENTRANCE(),
  fadeInLeft: ENTRANCE(),
  fadeInRight: ENTRANCE(),
  slideInUp: ENTRANCE(),
  slideInDown: ENTRANCE(),
  slideInLeft: ENTRANCE(),
  slideInRight: ENTRANCE(),
  scaleIn: ENTRANCE(400),
  scaleInUp: ENTRANCE(),
  scaleInDown: ENTRANCE(),
  bounceIn: ENTRANCE(600),
  bounceInUp: ENTRANCE(600),
  bounceInDown: ENTRANCE(600),
  expandIn: ENTRANCE(400),
  revealUp: ENTRANCE(500),
  revealDown: ENTRANCE(500),
  flipInX: ENTRANCE(600),
  flipInY: ENTRANCE(600),
  zoomIn: ENTRANCE(400),
  zoomInUp: ENTRANCE(500),
  zoomInDown: ENTRANCE(500),
  dropIn: ENTRANCE(600),
  riseUp: ENTRANCE(500),
  typewriter: ENTRANCE(900),

  // ── Emphasis (14) ─────────────────────────────────────────────
  pulse: EMPHASIS(),
  bounce: EMPHASIS(),
  shake: EMPHASIS(600),
  shakeX: EMPHASIS(600),
  shakeY: EMPHASIS(600),
  wobble: EMPHASIS(),
  swing: EMPHASIS(),
  rubberBand: EMPHASIS(),
  jello: EMPHASIS(900),
  heartbeat: EMPHASIS(1200),
  flash: EMPHASIS(),
  tada: EMPHASIS(),
  headShake: EMPHASIS(700),
  pop: EMPHASIS(400),

  // ── Continuous / loop (12) ────────────────────────────────────
  spin: { category: 'loop', defaultDurationMs: 1500, defaultEasing: 'linear' },
  float: LOOP(3000),
  breathe: LOOP(3000),
  glow: LOOP(),
  shimmer: LOOP(),
  pendulum: LOOP(2000),
  blink: LOOP(1600),
  sway: LOOP(2800),
  wave: LOOP(2200),
  pulseShadow: LOOP(2000),
  morphBackground: LOOP(4000),
  rotateLoop: { category: 'loop', defaultDurationMs: 4000, defaultEasing: 'linear' },
};

const warnedPresets = new Set<string>();

/**
 * Resolve a preset name to its definition, or null for out-of-scope /
 * unknown values (one-time console.warn per value; exit presets are silent —
 * they are valid schema, just deferred with the onExit trigger).
 */
export function resolvePreset(preset: string): PresetDefinition | null {
  const def = PRESET_REGISTRY[preset];
  if (def) return def;
  const isExitPreset = /Out|collapseOut/.test(preset);
  if (!isExitPreset && !warnedPresets.has(preset)) {
    warnedPresets.add(preset);
    console.warn(`[ElementAnimation] Unknown animation preset "${preset}" — skipping animation`);
  }
  return null;
}

/** CSS class applied for a preset (see element-animations.css) */
export function presetClassName(preset: string): string {
  return `sv2-anim-${preset}`;
}
